/**
 * Pure logic for the GloryBoard Ranking Window field: an amount plus a unit (minutes, hours, days).
 *
 * The server stores ONE figure, `gloryBoardWindowHours`, and honours it to the minute — so 30
 * minutes travels as `0.5` and two days as `48`. The unit is a display choice only and is never
 * saved; on load it is re-derived as the largest unit the stored length divides into evenly.
 */

export const WINDOW_UNITS = [
  { key: 'minutes', label: 'Min', minutes: 1 },
  { key: 'hours', label: 'Hours', minutes: 60 },
  { key: 'days', label: 'Days', minutes: 24 * 60 }
];

const unitOf = (key) => WINDOW_UNITS.find((u) => u.key === key) || WINDOW_UNITS[1];

/** The stored hours as the amount + unit an admin would have typed, e.g. 48 → 2 days. */
export const windowFromHours = (hours) => {
  const totalMinutes = Math.round(Number(hours) * 60);
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return { amount: '', unit: 'hours' };

  const unit = [...WINDOW_UNITS].reverse().find((u) => totalMinutes % u.minutes === 0);
  return { amount: String(totalMinutes / unit.minutes), unit: unit.key };
};

/**
 * Whole minutes for an amount + unit, or null when the entry is not a usable length — blank, not a
 * number, under a minute, or landing between two minutes (e.g. 0.3 minutes, 1.001 hours).
 */
export const windowMinutes = (amount, unitKey) => {
  if (amount === '' || amount === null || amount === undefined) return null;
  const raw = Number(amount) * unitOf(unitKey).minutes;
  if (!Number.isFinite(raw)) return null;

  const minutes = Math.round(raw);
  if (minutes < 1 || Math.abs(raw - minutes) > 1e-6) return null;
  return minutes;
};

/** What `gloryBoardWindowHours` should be saved as, or null when the entry is not valid. */
export const windowToHours = (amount, unitKey) => {
  const minutes = windowMinutes(amount, unitKey);
  return minutes === null ? null : minutes / 60;
};

/** A plain-English reading of a length, e.g. 90 → "1 hour 30 minutes", for the field's hint. */
export const describeMinutes = (totalMinutes) => {
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const part = (n, word) => (n ? `${n} ${word}${n === 1 ? '' : 's'}` : null);
  return [part(days, 'day'), part(hours, 'hour'), part(minutes, 'minute')].filter(Boolean).join(' ');
};

/**
 * What the administrator is asked before saving a changed length, or null when it has not changed.
 *
 * A save applies at once — the server re-times every running window from its own halftime — so a
 * slip in this field moves contests fans are already playing. Hence a question, not just a hint.
 */
export const windowChangeConfirmation = (fromHours, toHours) => {
  const from = Math.round(Number(fromHours) * 60);
  const to = Math.round(Number(toHours) * 60);
  if (!Number.isFinite(to) || to < 1 || from === to) return null;

  const toText = describeMinutes(to);
  const fromText = Number.isFinite(from) && from >= 1 ? ` from ${describeMinutes(from)}` : '';
  return (
    `Change the GloryBoard Ranking Window${fromText} to ${toText}?\n\n` +
    `This applies now. Windows already running will close ${toText} after their match's ` +
    `halftime, and any that have already run longer than that will close immediately.`
  );
};

/** The save alert's account of what a new length did to running windows, or '' when nothing. */
export const describeAppliedWindows = (applied) => {
  const updated = applied?.updated || 0;
  if (!updated) return '';

  const closedNow = applied.closedNow || 0;
  const windows = `${updated} running GloryBoard window${updated === 1 ? '' : 's'}`;
  if (closedNow === updated) return `${windows} closed now, having already run longer than that.`;
  if (closedNow) return `${windows} updated; ${closedNow} of them closed now.`;
  return `${windows} updated to the new length.`;
};
